import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Plus, GripVertical, Save, BarChart3 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import SurveyResults from "./SurveyResults";

interface SurveyQuestion {
  id: string;
  question: string;
  type: "rating" | "text";
  required: boolean;
}

interface SurveySettings {
  id: string;
  is_active: boolean;
  title: string;
  description: string | null;
  questions: SurveyQuestion[];
}

const SurveySettingsComponent = () => {
  const [settings, setSettings] = useState<SurveySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('survey_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings({
          ...data,
          questions: (data.questions as unknown as SurveyQuestion[]) || []
        });
      }
    } catch (error) {
      console.error('Error loading survey settings:', error);
      toast.error('Fehler beim Laden der Umfrage-Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('survey_settings')
        .update({
          is_active: settings.is_active,
          title: settings.title,
          description: settings.description,
          questions: JSON.parse(JSON.stringify(settings.questions))
        })
        .eq('id', settings.id);

      if (error) throw error;

      toast.success('Umfrage-Einstellungen gespeichert');
    } catch (error) {
      console.error('Error saving survey settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const addQuestion = () => {
    if (!settings || settings.questions.length >= 10) {
      toast.error('Maximal 10 Fragen erlaubt');
      return;
    }

    const newQuestion: SurveyQuestion = {
      id: `q${Date.now()}`,
      question: "",
      type: "rating",
      required: true
    };

    setSettings({
      ...settings,
      questions: [...settings.questions, newQuestion]
    });
  };

  const updateQuestion = (index: number, field: keyof SurveyQuestion, value: string | boolean) => {
    if (!settings) return;

    const newQuestions = [...settings.questions];
    newQuestions[index] = { ...newQuestions[index], [field]: value };
    setSettings({ ...settings, questions: newQuestions });
  };

  const removeQuestion = (index: number) => {
    if (!settings) return;

    const newQuestions = settings.questions.filter((_, i) => i !== index);
    setSettings({ ...settings, questions: newQuestions });
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index || !settings) return;

    const newQuestions = [...settings.questions];
    const draggedQuestion = newQuestions[draggedIndex];
    newQuestions.splice(draggedIndex, 1);
    newQuestions.splice(index, 0, draggedQuestion);

    setSettings({ ...settings, questions: newQuestions });
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Wird geladen...</div>
      </div>
    );
  }

  if (!settings) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Keine Umfrage-Einstellungen gefunden</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Tabs defaultValue="settings" className="space-y-6">
      <TabsList>
        <TabsTrigger value="settings">Einstellungen</TabsTrigger>
        <TabsTrigger value="results" className="gap-2">
          <BarChart3 className="h-4 w-4" />
          Auswertung
        </TabsTrigger>
      </TabsList>

      <TabsContent value="settings" className="space-y-6">
        {/* Status Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Umfrage-Status</CardTitle>
                <CardDescription>Aktivieren oder deaktivieren Sie die Mitarbeiterumfrage</CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={settings.is_active ? "default" : "secondary"}>
                  {settings.is_active ? "Aktiv" : "Inaktiv"}
                </Badge>
                <Switch
                  checked={settings.is_active}
                  onCheckedChange={(checked) => setSettings({ ...settings, is_active: checked })}
                />
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* General Settings */}
        <Card>
          <CardHeader>
            <CardTitle>Allgemeine Einstellungen</CardTitle>
            <CardDescription>Titel und Beschreibung der Umfrage</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titel</Label>
              <Input
                id="title"
                value={settings.title}
                onChange={(e) => setSettings({ ...settings, title: e.target.value })}
                placeholder="Titel der Umfrage"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Beschreibung</Label>
              <Textarea
                id="description"
                value={settings.description || ""}
                onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                placeholder="Beschreibung für die Mitarbeiter"
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Questions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Fragen ({settings.questions.length}/10)</CardTitle>
                <CardDescription>
                  Verwalten Sie die Umfragefragen. Bewertungsfragen nutzen eine 1-5 Skala.
                </CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={addQuestion}
                disabled={settings.questions.length >= 10}
              >
                <Plus className="h-4 w-4 mr-2" />
                Frage hinzufügen
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {settings.questions.map((question, index) => (
                <div
                  key={question.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-start gap-3 p-4 border rounded-lg bg-card ${
                    draggedIndex === index ? 'opacity-50' : ''
                  }`}
                >
                  <div className="cursor-grab pt-2">
                    <GripVertical className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">Frage {index + 1}</Badge>
                      <Select
                        value={question.type}
                        onValueChange={(value: "rating" | "text") => updateQuestion(index, 'type', value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="rating">Bewertung (1-5)</SelectItem>
                          <SelectItem value="text">Freitext</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-2 ml-auto">
                        <Label htmlFor={`required-${index}`} className="text-sm text-muted-foreground">
                          Pflichtfeld
                        </Label>
                        <Switch
                          id={`required-${index}`}
                          checked={question.required}
                          onCheckedChange={(checked) => updateQuestion(index, 'required', checked)}
                        />
                      </div>
                    </div>
                    <Input
                      value={question.question}
                      onChange={(e) => updateQuestion(index, 'question', e.target.value)}
                      placeholder="Frage eingeben..."
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeQuestion(index)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}

              {settings.questions.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  Keine Fragen vorhanden. Klicken Sie auf "Frage hinzufügen" um zu beginnen.
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
          </Button>
        </div>
      </TabsContent>

      <TabsContent value="results">
        <SurveyResults />
      </TabsContent>
    </Tabs>
  );
};

export default SurveySettingsComponent;
